import React from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import useSWR from 'swr';
import api, {getURL} from 'api';
import {Deal, Offer} from '../../../utils/upselling/types';
import {PATHS} from '../../../Routes';
import {OFFER_CONFIRMATION_TYPES, OFFER_LABELS} from '../../../utils/upselling';
import {Guest} from '../../../utils/types';
import {
  useErrorModal,
  useIsMounted,
  useModalControls,
  useStatus,
} from '../../../utils/hooks';
import {useOfferAndSupplier, useCartDeals} from '../../../hooks';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {useTranslation} from 'react-i18next';
import {getFinishCheckinHistoryArgs} from '../../../utils/checkin';
import {getStoredGuestId, setStoredGuestId} from '../../../utils/guests';
import {TempWelcomePickupsOfferData} from '../ExperiencesPage';
import bookedIcon from '../../../assets/img/icon-booked.png';
import backgroundPlaceholder from '../../../assets/empty-offer-background.svg';
import Page from 'components/acommon/Page';
import Button, {DesktopBackButton} from 'components/Button';
import WelcomePickupsForm from '../orderForms/WelcomePickupsForm';
import {EmailRequestModal} from '../../../components/EmailRequestModal/EmailRequestModal';
import {CaseHeader} from '../shared';
import {UpsellingOrderForm} from '../orderForms';
import {StyledPage} from '../styled';
import {CaseBody, Col, Image, LargeLabel, ModalSuccessfullyRequested} from './styled';

function ExperiencePage() {
  const {t} = useTranslation();
  const {ErrorModal, displayError} = useErrorModal();
  const isMounted = useIsMounted();
  const history = useHistory();
  const location = useLocation();
  const {offerId} = useParams<{offerId: string}>();
  const reservationDetails = useComputedReservationDetails();
  const {isLoading: isCreatingDeal, setStatus: setDealCreationStatus} = useStatus();
  const isWelcomePickupsOffer = offerId === TempWelcomePickupsOfferData.id;
  const {offer: offerRes, isLoadingOfferOrSupplier} = useOfferAndSupplier(
    isWelcomePickupsOffer ? '' : offerId,
    {
      onError: displayError,
    },
  );
  const offer = isWelcomePickupsOffer ? (TempWelcomePickupsOfferData as Offer) : offerRes;
  const payingGuestIdRef = React.useRef<Guest | null>(null);
  const enterGuestMailRef = React.useRef<string | null>(null);
  const onSuccessRef = React.useRef<any | null>(null);
  const dealPayloadRef = React.useRef<any | null>(null);
  const {setCartDealToStorage, createCartDeals, storedCartDeals, isCreatingCartDeals} =
    useCartDeals({
      onError: displayError,
    });
  const selectedLabel = storedCartDeals[offer?.id || offerId] && OFFER_LABELS.selected;
  const label = selectedLabel || '';
  const {
    isOpen: isEmailRequestModalOpen,
    openModal: openEmailRequestModal,
    closeModal: closeEmailRequestModal,
  } = useModalControls();
  const {
    isOpen: isOfferSuccessfullyRequestedModalOpen,
    closeModal: closeIsOfferSuccessfullyRequestedModal,
    openModal: openIsOfferSuccessfullyRequestedModal,
  } = useModalControls();

  const imageUrl = !isLoadingOfferOrSupplier
    ? offer?.image_url || backgroundPlaceholder
    : '';

  const guestId = getStoredGuestId() || payingGuestIdRef.current?.id;
  const {
    data: guest,
    isValidating: isLoadingGuestId,
    mutate: mutateGuest,
  } = useSWR(guestId ? getURL(api.guests.ENDPOINTS.one(guestId)) : null, {
    onError: error => {
      if (!isMounted.current) {
        return;
      }

      // if (isPayingGuestSelectionModalOpen) {
      displayError(error);
      // closePayingGuestSelectionModal();
      // }

      setStoredGuestId('');
    },
  });

  const isLoading = isLoadingGuestId || isCreatingDeal || isCreatingCartDeals;

  const leaderId = reservationDetails.reservation?.guest_group?.leader_id;
  // const guestEmail =
  //   guest?.email || enterGuestMailRef.current || payingGuestIdRef.current?.email;

  // const [isOpenModalOrderDetails, setOpenModalOrderDetails] =
  //   React.useState<boolean>(false);

  // const handleModalOrderDetails = () => {
  //   setOpenModalOrderDetails(!isOpenModalOrderDetails);
  // };

  const createDeal = async (payload: Deal) => {
    setDealCreationStatus('loading');
    const {error} = await api.upselling.createDeal(payload);
    setDealCreationStatus('idle');

    if (!isMounted.current) {
      return true;
    }

    if (error) {
      displayError(error);
      return Boolean(error);
    }
  };

  const requestOffer = async (
    data: Deal = dealPayloadRef.current,
    onSuccess: () => void = onSuccessRef.current,
  ) => {
    const isMissingGuestEmail = !isEmailRequestModalOpen;
    // !guest?.email &&
    // !payingGuestIdRef.current?.email;
    if (isMissingGuestEmail) {
      dealPayloadRef.current = data;
      onSuccessRef.current = onSuccess;
      openEmailRequestModal();
      return;
    }

    const isError = await createDeal({
      ...data,
      guest_email: enterGuestMailRef.current || '',
    });
    if (!isError) {
      onSuccess();
      openIsOfferSuccessfullyRequestedModal();
    }
  };

  const goNextAfterUpselling = (dealsCreated = false) => {
    if (reservationDetails.hasTaxes) {
      history.push(PATHS.touristTaxes, location.state);
      return;
    }

    if (dealsCreated) {
      history.push(PATHS.pay, location.state);
      return;
    }

    const args = getFinishCheckinHistoryArgs({
      reservationDetails,
      skip: {
        hasUpselling: true,
      },
      locationState: location.state,
    });

    history.push(args.url, args.state);
  };

  const handlePayNow = async (deal: Deal, onSuccess: () => void) => {
    setCartDealToStorage(deal);

    const isError = await createCartDeals();
    if (!isError) {
      onSuccess();
      goNextAfterUpselling(true);
    }
  };

  const handleSubmit = (deal: Deal, onSuccess: () => void) => {
    if (!leaderId) return displayError(t('you_are_first_guest_register'));

    if (offer?.confirmation_type === OFFER_CONFIRMATION_TYPES.manual) {
      requestOffer(deal, onSuccess);
    } else {
      handlePayNow(deal, onSuccess);
    }
    // setOpenModalReceiveTicket(!isOpenModalReceiveTicket);
  };

  const handleBookAndKeepExploringSuccess = (deal: Deal) => {
    if (!leaderId) return displayError(t('you_are_first_guest_register'));

    setCartDealToStorage(deal);
    history.push(PATHS.experiences, location.state);
  };

  const handleKeepExploring = () => {
    closeIsOfferSuccessfullyRequestedModal();
    history.push(PATHS.experiences, location.state);
  };

  return (
    <Page showFooterOnlyMobile>
      {/* <ModalOrderDetails
        isOpenModalOrderDetails={isOpenModalOrderDetails}
        handleModalOrderDetails={handleModalOrderDetails}
        handleModalReceiveTicket={handleModalReceiveTicket}
        reservationId={reservationDetails?.reservation?.id}
        housingId={reservationDetails?.reservation?.housing?.id}
      /> */}
      {/*<ModalReceiveTicket*/}
      {/*  isOpenModalReceiveTicket={isOpenModalReceiveTicket}*/}
      {/*  handleModalReceiveTicket={handleModalReceiveTicket}*/}
      {/*  // reservationId={reservationDetails?.reservation?.id}*/}
      {/*  // housingId={reservationDetails?.reservation?.housing?.id}*/}
      {/*/>*/}

      <StyledPage>
        <DesktopBackButton />
        <CaseHeader title={offer?.title || ''} />
        <CaseBody>
          <Col style={{alignSelf: 'stretch', width: '100%'}}>
            <Image
              style={{
                backgroundImage: `url(${imageUrl})`,
              }}
            >
              {label && <LargeLabel value={label}>{t(label)}</LargeLabel>}
            </Image>
            {/* <Col className="paragraph">
              <div className="subTitle">Amazing Experience</div>
              <RoundBadges>
                <ExperienceProperty>
                  <StyledIcon name="circleTimeIcon" size="46" />
                  <div className="property">12 hours</div>
                </ExperienceProperty>
                <ExperienceProperty>
                  <StyledIcon name="circlePeopleIcon" size="46" />
                  <div className="property">Up to 4 people</div>
                </ExperienceProperty>

                <ExperienceProperty>
                  <StyledIcon name="circleLanguageIcon" size="46" />
                  <div className="property">Hosted in English, Spanish</div>
                </ExperienceProperty>
              </RoundBadges>
            </Col> */}
            <Col className="paragraph">
              <div className="subTitle">{t('description')}</div>
              <div className="text18">{offer?.description}</div>
            </Col>
          </Col>
          <Col style={{width: '100%', maxWidth: '480px'}}>
            {isWelcomePickupsOffer && <WelcomePickupsForm />}
            {offer && !isWelcomePickupsOffer && (
              <UpsellingOrderForm
                offerId={offerId}
                offerData={offer}
                guest={guest}
                disabled={isLoading}
                handleSubmitForm={handleSubmit}
                onBookAndKeepExploring={handleBookAndKeepExploringSuccess}
                enterGuestMail={enterGuestMailRef.current}
              />
            )}
          </Col>
        </CaseBody>
        {/* FUTURE FEATURE */}
        {/* <CaseRecommendations /> */}
      </StyledPage>
      <ModalSuccessfullyRequested
        closeOnDocumentClick
        closeOnEscape
        open={isOfferSuccessfullyRequestedModalOpen}
        onClose={closeIsOfferSuccessfullyRequestedModal}
        title={t('booked_exclamation')}
        text={t('offer_requested_text')}
        iconProps={{
          src: bookedIcon,
          alt: '',
        }}
      >
        <Button
          style={{margin: '0 auto', justifyContent: 'center'}}
          className="buttonSuccessRequestedModal"
          onClick={handleKeepExploring}
          label={t('keep_exploring')}
        />
      </ModalSuccessfullyRequested>
      {isEmailRequestModalOpen && leaderId && (
        <EmailRequestModal
          guestId={leaderId}
          mutateGuest={mutateGuest}
          onClose={closeEmailRequestModal}
          enterGuestMailRef={enterGuestMailRef}
          onSuccess={requestOffer}
        />
      )}
      <ErrorModal />
    </Page>
  );
}

export default ExperiencePage;
